import { useEffect, useRef } from 'react';

/* ─────────────────────────────────────────────────────────────
   Reusable ambient particle canvas.
   Sits absolutely over its parent (parent must be position:relative / overflow:hidden).
   Pauses via IntersectionObserver when off-screen.
───────────────────────────────────────────────────────────────*/

const PRESETS = {
  /* About — gold dust motes drifting upward */
  dust: {
    count:    65,
    colors:   ['#c9a84c', '#e0c070', '#d4a840', '#b89040'],
    minR: 0.6, maxR: 2.2,
    minDx: -0.12, maxDx: 0.12,
    minDy: -0.45, maxDy: -0.12,   // upward
    wobbleAmp: 0.3,
    maxAlpha: 0.55,
    glow: 6,
  },
  /* Services — slow drifting crimson sparks */
  sparks: {
    count:    42,
    colors:   ['#8b0000', '#c0002a', '#c9a84c', '#a01020'],
    minR: 0.7, maxR: 2.6,
    minDx: 0.08, maxDx: 0.30,    // rightward drift
    minDy: -0.18, maxDy: 0.18,
    wobbleAmp: 0.6,
    maxAlpha: 0.42,
    glow: 10,
  },
  /* Contact — fine ash falling */
  ash: {
    count:    50,
    colors:   ['#a89eac', '#c4b8c0', '#8a7e8e', '#ccc0c8'],
    minR: 0.5, maxR: 1.8,
    minDx: -0.18, maxDx: 0.18,
    minDy: 0.18, maxDy: 0.55,    // downward
    wobbleAmp: 0.25,
    maxAlpha: 0.38,
    glow: 4,
  },
};

function rand(min, max) { return min + Math.random() * (max - min); }

function makeParticle(p, w, h, respawn = false) {
  return {
    x: rand(0, w),
    y: respawn
      ? (p.minDy < 0 ? h + 10 : -10)   // re-enter from the edge they exit through
      : rand(0, h),
    r:       rand(p.minR, p.maxR),
    dx:      rand(p.minDx, p.maxDx),
    dy:      rand(p.minDy, p.maxDy),
    wobble:  rand(0, Math.PI * 2),
    wobbleSpeed: rand(0.008, 0.025),
    wobbleAmp:   p.wobbleAmp,
    alpha:   rand(0.05, p.maxAlpha),
    fade:    rand(0.0008, 0.002),
    fadeDir: 1,
    color:   p.colors[Math.floor(Math.random() * p.colors.length)],
    glow:    p.glow,
    maxAlpha: p.maxAlpha,
  };
}

export default function ParticleCanvas({ preset = 'dust' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const p   = PRESETS[preset];

    let w = canvas.width  = canvas.offsetWidth;
    let h = canvas.height = canvas.offsetHeight;

    let particles = Array.from({ length: p.count }, () => makeParticle(p, w, h));
    let raf       = null;
    let running   = false;

    function draw() {
      ctx.clearRect(0, 0, w, h);

      for (const e of particles) {
        // gentle fade pulse
        e.alpha += e.fade * e.fadeDir;
        if (e.alpha >= e.maxAlpha) e.fadeDir = -1;
        if (e.alpha <= 0.02)       e.fadeDir =  1;

        e.wobble += e.wobbleSpeed;
        e.x += e.dx + Math.sin(e.wobble) * e.wobbleAmp;
        e.y += e.dy;

        // respawn when off-canvas
        const gone = p.minDy < 0
          ? e.y < -10                        // upward preset — gone off top
          : e.y > h + 10;                    // downward — gone off bottom
        const goneSide = e.x < -10 || e.x > w + 10;

        if (gone || goneSide) {
          Object.assign(e, makeParticle(p, w, h, true));
          if (goneSide) e.y = rand(0, h);    // side exit → random y on re-entry
        }

        ctx.save();
        ctx.globalAlpha = Math.max(0, e.alpha);
        ctx.shadowBlur  = e.glow;
        ctx.shadowColor = e.color;
        ctx.fillStyle   = e.color;
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      raf = requestAnimationFrame(draw);
    }

    function start() { if (!running) { running = true; draw(); } }
    function stop()  { running = false; cancelAnimationFrame(raf); }

    // only animate when section is visible
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting ? start() : stop(),
      { threshold: 0.05 }
    );
    observer.observe(canvas);

    const onResize = () => {
      w = canvas.width  = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      particles = Array.from({ length: p.count }, () => makeParticle(p, w, h));
    };
    window.addEventListener('resize', onResize);

    return () => {
      stop();
      observer.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [preset]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  );
}
